import RouteTemplate from 'ember-route-template'
import type WritingLegendsVocabularyBankRoute from 'teacher-ui-client/reporting/ui-scope/writing-legends/vocabulary/bank/route'
import {
  AverageWordUsage,
  AverageDeviationAlerts,
  VocabularyAnalyserChart,
} from 'teacher-ui-client/components/writing-legends/charts/vocabulary-analyser-chart'
import { toRequestScope, type BaseReport, type BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import Component from '@glimmer/component'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { trackedFunction } from 'reactiveweb/function'
import { Product } from 'teacher-ui-client/utils/products'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { keepLatest } from 'reactiveweb/keep-latest'
import { t } from 'ember-intl'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { ErrorsUnexpected } from 'teacher-ui-client/components/errors/unexpected'
import NoDataMessage from 'teacher-ui-client/components/no-data-message'

export interface LanguageGemsAnalyserData extends BaseReport {
  language_gems_stats: {
    students: {
      student_id: string
      student_blake_id: number
      school_blake_id: number
      rate: number
      first_name: string
      last_name: string
      grade_position: number
      grade_position_adjusted: number // this field is generated by the backend for supported grade positions (2 to 8)
    }[]
    trendline: {
      grade_position: number
      rate: number
    }[]
  }
  class_grade_averages: {
    grade_position: number
    rate: number
  }[]
  meta: BaseReportMetaPaging
}

interface Signature {
  Args: {
    model: ModelFor<WritingLegendsVocabularyBankRoute>
  }
}

export class WLVocabularyLanguageGemsAnalyserPage extends Component<Signature> {
  @service reporting!: ReportingService

  fetchData = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.model.uiScope)

    const url = this.reporting.buildWritingLegendsUrl({
      product: Product.WL,
      requestScope,
      reportName: 'vocabulary/language-gems-analyser',
    })

    const query = this.reporting.buildQuery({
      period: this.args.model.period,
    })

    return this.reporting.fetchReport<LanguageGemsAnalyserData>(url, query)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  get isUnsupportedScope() {
    const requestScope = toRequestScope(this.args.model.uiScope)
    return requestScope.scope !== 'school-class'
  }

  <template>
    {{#if this.isUnsupportedScope}}
      <NoDataMessage
        @compact={{true}}
        @body={{t "components.noDataMessage.limitedScope" scope="class" htmlSafe=true}}
        class="m-4"
      />
    {{else if this.latestData}}
      <p class="p-8 text-center">{{t "pages.wlVocabulary.languageGemsDescription"}}</p>
      <VocabularyAnalyserChart
        class="h-96"
        @higherIsBetter={{true}}
        @yAxisTitle="Language gems used (%)"
        @trendline={{this.latestData.language_gems_stats.trendline}}
        @studentsData={{this.latestData.language_gems_stats.students}}
        @classGradeAverages={{this.latestData.class_grade_averages}}
      />

      <div class="mx-auto w-2/3 space-y-10">
        <AverageDeviationAlerts
          @higherIsBetter={{true}}
          @trendline={{this.latestData.language_gems_stats.trendline}}
          @studentsData={{this.latestData.language_gems_stats.students}}
          @classGradeAverages={{this.latestData.class_grade_averages}}
        />

        <AverageWordUsage
          @message={{t "pages.wlVocabulary.studentsUseMoreLanguageGems"}}
          @studentsData={{this.latestData.language_gems_stats.students}}
          @classGradeAverages={{this.latestData.class_grade_averages}}
          @showAboveAverage={{false}}
        />
      </div>
    {{else if this.fetchData.isPending}}
      <FidgetSpinnerWaveComponent @centered={{true}} />
    {{else if this.fetchData.isError}}
      <ErrorsUnexpected class="m-4" data-test-usage-error />
    {{else}}
      <NoDataMessage class="m-4" data-test-usage-empty />
    {{/if}}
  </template>
}

export default RouteTemplate(WLVocabularyLanguageGemsAnalyserPage)
